import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { ResponsiveBar } from "@nivo/bar";

// Maps raw cdr.status codes to friendly labels + a category color. Codes
// outside this map render with the raw code + neutral color so a new status
// emitted by the routing engine never crashes the chart.
//
// STATUS_META is keyed on the canonical (current re_fastagi) status string.
// STATUS_ALIAS rewrites legacy long forms onto their canonical short form so
// historical CDR rows still aggregate into the same chart segment after the
// rename. IVR_AUTHENDICATION_FAILED was always wrong on the frontend and
// never appeared in the DB, but is mapped defensively just in case.
const STATUS_META = {
  BUYER_ANSWERED:    { label: "Answered",            color: "#2e7d32" },
  BUYER_BUSY:        { label: "Busy",                color: "#f59e0b" },
  BUYER_NOANSWER:    { label: "No Answer",           color: "#fb923c" },
  BUYER_NOT_FOUND:   { label: "Buyer Not Found",     color: "#ef4444" },
  CALLER_CANCEL:     { label: "Caller Cancel",       color: "#a78bfa" },
  COMPAIGN_USER_MISMATCH: { label: "Campaign Mismatch", color: "#dc2626" },
  DID_COMPAIGN_MISSING:   { label: "No Campaign",       color: "#dc2626" },
  NON_OFFICE_HOUR:   { label: "Non Office Hour",     color: "#94a3b8" },
  NO_FUNDS:          { label: "Insufficient Funds",  color: "#dc2626" },
  NO_BILL_INFO:      { label: "No Billing Info",     color: "#dc2626" },
  CALLERID_BLOCKED:  { label: "Blocked",             color: "#7c3aed" },
  CONGESTION:        { label: "Congestion",          color: "#dc2626" },
  DID_NOT_ACTIVE:    { label: "DID Inactive",        color: "#dc2626" },
  DID_USER_NOT_ACTIVE:{ label: "User Inactive",      color: "#dc2626" },
  IVR_AUTH_FAILED:   { label: "IVR Auth Failed",     color: "#dc2626" },
  TRIED_NO_SUCCESS:  { label: "Tried No Success",    color: "#dc2626" },
  USER_NOT_FOUND:    { label: "User Not Found",      color: "#dc2626" },
};

const STATUS_ALIAS = {
  CALLERID_BLOCKED_BY_USER: "CALLERID_BLOCKED",
  NOT_SUFFICIENT_FUNDS: "NO_FUNDS",
  BILLING_INFO_NOT_FOUND: "NO_BILL_INFO",
  IVR_AUTHENDICATION_FAILED: "IVR_AUTH_FAILED",
  IVR_AUTHENTICATION_FAILED: "IVR_AUTH_FAILED",
};

const canonicalCode = (code) => STATUS_ALIAS[code] || code;

const NEUTRAL_COLOR = "#64748b";

// Mix a hex color with white by `amount` (0..1). Used to produce the lighter
// top-of-bar tone for the 3D-look gradient. Pure stdlib — no color libraries.
function lighten(hex, amount = 0.35) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return hex;
  const r = parseInt(m[1], 16);
  const g = parseInt(m[2], 16);
  const b = parseInt(m[3], 16);
  const lr = Math.round(r + (255 - r) * amount);
  const lg = Math.round(g + (255 - g) * amount);
  const lb = Math.round(b + (255 - b) * amount);
  return `#${((1 << 24) + (lr << 16) + (lg << 8) + lb).toString(16).slice(1)}`;
}

// Stack order — Answered sits at the bottom of every column so the eye reads
// "good calls" as the foundation, misses in the middle (ambers/purples), and
// outright failures at the top (reds). Statuses outside this list fall to
// the very top under "Other".
const STACK_ORDER = [
  "BUYER_ANSWERED",
  "BUYER_BUSY",
  "BUYER_NOANSWER",
  "CALLER_CANCEL",
  "BUYER_NOT_FOUND",
  "NON_OFFICE_HOUR",
  "CALLERID_BLOCKED",
  "NO_FUNDS",
  "NO_BILL_INFO",
  "COMPAIGN_USER_MISMATCH",
  "DID_COMPAIGN_MISSING",
  "DID_NOT_ACTIVE",
  "DID_USER_NOT_ACTIVE",
  "USER_NOT_FOUND",
  "IVR_AUTH_FAILED",
  "TRIED_NO_SUCCESS",
  "CONGESTION",
];

const labelFor = (code) => STATUS_META[canonicalCode(code)]?.label || code;
const colorFor = (code) => STATUS_META[canonicalCode(code)]?.color || NEUTRAL_COLOR;

function HangupReasonBar({ records = [], isDark = false }) {
  const { data, keys, keysToCode, total, dailyTotals, gradientDefs, gradientFill } = useMemo(() => {
    // Build the 7 day buckets up front (today − 6 ... today). Pre-creating
    // them guarantees every day shows on the x-axis even with zero calls.
    const days = [];
    const now = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      d.setHours(0, 0, 0, 0);
      const key = d.toISOString().slice(0, 10); // YYYY-MM-DD
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      days.push({ key, label: `${mm}/${dd}` });
    }
    const dayIndex = new Map(days.map((d, i) => [d.key, i]));

    // Tally counts per (day, status). Skip records without start_at or
    // outside the 7-day window.
    const earliest = new Date(now);
    earliest.setDate(now.getDate() - 6);
    earliest.setHours(0, 0, 0, 0);

    const matrix = days.map((d) => ({ day: d.label, _key: d.key }));
    const seenStatuses = new Set();

    for (const r of records) {
      const ts = r?.start_at;
      if (!ts) continue;
      const d = new Date(ts);
      if (isNaN(d) || d < earliest) continue;
      const dayKey = d.toISOString().slice(0, 10);
      const idx = dayIndex.get(dayKey);
      if (idx === undefined) continue;
      // Resolve legacy long forms to their canonical short codes so old +
      // new rows aggregate into the same stack segment.
      const code = canonicalCode(r?.status || "UNKNOWN");
      const label = labelFor(code);
      matrix[idx][label] = (matrix[idx][label] || 0) + 1;
      seenStatuses.add(code);
    }

    // Build the ordered list of status keys that actually have data so the
    // legend doesn't show 16 entries when only 4 are non-zero.
    const ordered = STACK_ORDER.filter((code) => seenStatuses.has(code));
    const extras = [...seenStatuses].filter((c) => !STACK_ORDER.includes(c));
    const orderedCodes = [...ordered, ...extras];
    const orderedKeys = orderedCodes.map(labelFor);

    // Map label → code (for color lookup, since Nivo passes the key/label,
    // not the original code, into the colors callback).
    const keysToCode = Object.fromEntries(orderedCodes.map((c) => [labelFor(c), c]));

    // Per-day totals power the optional total-per-bar label and tooltip.
    const dailyTotals = matrix.map((row) =>
      orderedKeys.reduce((s, k) => s + (row[k] || 0), 0)
    );
    const total = dailyTotals.reduce((a, b) => a + b, 0);

    // Build vertical "lit-from-above" gradient defs for the 3D-look bars.
    // One def + one fill rule per series key. Lighter tone at top fades to
    // base color at bottom — eye reads this as depth/sheen.
    const gradientDefs = orderedCodes.map((code) => {
      const base = colorFor(code);
      return {
        id: `hrGrad-${code}`,
        type: "linearGradient",
        x1: 0, y1: 0, x2: 0, y2: 1,
        colors: [
          { offset: 0,   color: lighten(base, 0.38) },
          { offset: 100, color: base },
        ],
      };
    });
    const gradientFill = orderedCodes.map((code) => ({
      match: { id: labelFor(code) },
      id: `hrGrad-${code}`,
    }));

    return { data: matrix, keys: orderedKeys, keysToCode, total, dailyTotals, gradientDefs, gradientFill };
  }, [records]);

  if (total === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="body2" sx={{ color: isDark ? "#8899aa" : "#999" }}>
          No call data available
        </Typography>
      </Box>
    );
  }

  const axisColor = isDark ? "#b0bec5" : "#555";
  const gridColor = isDark ? "#2a3a4a" : "#e5e7eb";
  const textColor = isDark ? "#e0e0e0" : "#1f2937";
  const subtleColor = isDark ? "#b0bec5" : "#64748b";

  return (
    <Box sx={{ height: "380px", display: "flex", flexDirection: "column" }}>
      {/* drop-shadow filter on the chart container lifts every bar off the
          surface — combined with the per-bar gradients and a darker border
          modifier, gives a 3D-looking stacked bar without rotating the SVG
          (which would distort the axis labels).
          Animation: every bar segment grows from its baseline (scaleY 0→1)
          over 1.6s. transform-box: fill-box anchors the transform origin to
          the rect's own bottom edge (without it, SVG transforms use the SVG
          root which would collapse bars upward). nth-of-type stagger gives
          the chart a left-to-right "filling in" sequence so the columns
          don't all rise simultaneously — they cascade. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          filter: "drop-shadow(0 6px 8px rgba(0,0,0,0.18))",
          "& rect": {
            transformOrigin: "center bottom",
            transformBox: "fill-box",
            animation: "hrBarRise 1.6s cubic-bezier(0.16, 1, 0.3, 1) backwards",
          },
          "& rect:nth-of-type(7n+1)": { animationDelay: "0.00s" },
          "& rect:nth-of-type(7n+2)": { animationDelay: "0.12s" },
          "& rect:nth-of-type(7n+3)": { animationDelay: "0.24s" },
          "& rect:nth-of-type(7n+4)": { animationDelay: "0.36s" },
          "& rect:nth-of-type(7n+5)": { animationDelay: "0.48s" },
          "& rect:nth-of-type(7n+6)": { animationDelay: "0.60s" },
          "& rect:nth-of-type(7n+7)": { animationDelay: "0.72s" },
          "@keyframes hrBarRise": {
            "0%":   { transform: "scaleY(0)", opacity: 0.4 },
            "60%":  { opacity: 1 },
            "100%": { transform: "scaleY(1)", opacity: 1 },
          },
        }}
      >
        <ResponsiveBar
          data={data}
          keys={keys}
          indexBy="day"
          groupMode="stacked"
          margin={{ top: 16, right: 20, bottom: 40, left: 48 }}
          padding={0.28}
          innerPadding={1}
          colors={(d) => colorFor(keysToCode[d.id])}
          // Each segment fills with a vertical gradient (light top → base
          // bottom) — the visual cue that sells "3D" without a perspective
          // tilt. Falls back to flat color if def is missing.
          defs={gradientDefs}
          fill={gradientFill}
          borderRadius={4}
          borderWidth={1}
          borderColor={{ from: "color", modifiers: [["darker", 0.45]] }}
          enableGridY
          gridYValues={5}
          axisBottom={{
            tickSize: 0,
            tickPadding: 8,
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 6,
            tickValues: 5,
            legend: "Calls",
            legendOffset: -38,
            legendPosition: "middle",
          }}
          // In-segment count labels — auto-hidden on segments too small to
          // legibly fit a number (Nivo handles via labelSkip*).
          labelSkipWidth={18}
          labelSkipHeight={14}
          labelTextColor="#fff"
          theme={{
            axis: {
              ticks: { text: { fill: axisColor, fontSize: 11 } },
              legend: { text: { fill: axisColor, fontSize: 12, fontWeight: 600 } },
            },
            grid: { line: { stroke: gridColor, strokeWidth: 1 } },
            labels: {
              text: {
                fontSize: 11,
                fontWeight: 700,
                textShadow: "0 1px 2px rgba(0,0,0,0.3)",
              },
            },
          }}
          tooltip={({ id, value, indexValue, color }) => {
            const dayIdx = data.findIndex((d) => d.day === indexValue);
            const dayTotal = dayIdx >= 0 ? dailyTotals[dayIdx] : 0;
            const pct = dayTotal > 0 ? Math.round((value / dayTotal) * 100) : 0;
            return (
              <Box
                sx={{
                  background: isDark ? "#1a2332" : "#ffffff",
                  color: textColor,
                  px: 2,
                  py: 1.2,
                  borderRadius: 1.5,
                  border: isDark ? "1px solid #2a3a4a" : "1px solid #e5e7eb",
                  boxShadow: `0 10px 24px ${color}55, 0 2px 8px rgba(0,0,0,0.18)`,
                  minWidth: 180,
                }}
              >
                <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: subtleColor, mb: 0.4 }}>
                  {indexValue} · {dayTotal} call{dayTotal === 1 ? "" : "s"}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: color,
                      flexShrink: 0,
                    }}
                  />
                  <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color }}>
                    {id}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: textColor, mt: 0.3 }}>
                  {value} call{value === 1 ? "" : "s"} · {pct}% of day
                </Typography>
              </Box>
            );
          }}
          motionConfig="gentle"
        />
      </Box>

      {/* Custom legend — only shows statuses that have data in the window,
          color matched to the stack segments. Wraps cleanly at narrow widths. */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: { xs: 1, sm: 1.5, md: 2 },
          pt: 1,
        }}
      >
        {keys.map((label) => {
          const code = keysToCode[label];
          const color = colorFor(code);
          return (
            <Box
              key={label}
              sx={{ display: "flex", alignItems: "center", gap: 0.6 }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: color,
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: textColor }}>
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default HangupReasonBar;
